import {
  differenceInDays,
  isToday,
  isTomorrow,
  add,
  format,
  startOfDay,
} from "date-fns";
import { dateFormats } from "../../common/styles";
import i18next, { TFunction } from "i18next";

const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
): string => {
  return date.toLocaleString(i18next.language, options);
};

type GetDateDropdownOptionsOpts = {
  futureDaysTotal: number;
  noticePeriodInHours: number;
  t: TFunction;
};

export const getDateDropdownOptions = (
  options: GetDateDropdownOptionsOpts,
): { optionId: string; value: string }[] => {
  const { futureDaysTotal, noticePeriodInHours, t } = options;

  const noticePeriodInDays = Math.floor(noticePeriodInHours / 24);

  const firstAvailableFutureDate = add(new Date(), {
    days: noticePeriodInDays,
  });

  const lastAvailableFutureDate = add(new Date(), {
    days: futureDaysTotal,
  });

  /**
   * Calculate the difference between the fist date option
   * & the last date option.
   *
   * We want the last available date to be x futureDaysTotal
   * days away from today's date.
   *
   * If noticePeriodInDays is 7 & futureDaysTotal is 14
   * there will be 7 options in the dropdown that
   * occur after the first available date.
   */
  const futureDaysDiffInDays = differenceInDays(
    lastAvailableFutureDate,
    firstAvailableFutureDate,
  );

  const dropdownOptions = [...Array(futureDaysDiffInDays).keys()].map(
    (index) => {
      const optionDate = add(firstAvailableFutureDate, {
        days: index,
      });

      const isOptionToday = isToday(optionDate);
      const isOptionTomorrow = isTomorrow(optionDate);

      const value = isOptionToday
        ? t("today").charAt(0).toUpperCase() + t("today").slice(1)
        : isOptionTomorrow
        ? t("tomorrow").charAt(0).toUpperCase() + t("tomorrow").slice(1)
        : formatDate(optionDate, dateFormats.weekdayDayAndMonth);

      const optionId = format(startOfDay(optionDate), "yyyy-MM-dd");

      return { optionId, value };
    },
  );

  return dropdownOptions;
};
